import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import "../../assets/styles/table.css";
import "../../assets/styles/category.css";

const getRowId = (row) => String(row._id || row.id);

const renderCellValue = (row, column) => {
  if (column.render) {
    return column.render(row[column.key], row);
  }

  if (column.key === "status") {
    const statusValue = String(row[column.key] || "").toLowerCase();
    let statusClass = "";
    switch (statusValue) {
      case "active":
        statusClass = "status-active";
        break;
      case "draft":
        statusClass = "status-draft";
        break;
      case "pending":
        statusClass = "status-pending";
        break;
      case "expired":
        statusClass = "status-expired";
        break;
      case "disabled":
        statusClass = "status-disabled";
        break;
      case "trial":
        statusClass = "status-trial";
        break;
      default:
        statusClass = "status-default";
    }
    return (
      <span className={`status-badge ${statusClass}`}>{row[column.key]}</span>
    );
  }

  return row[column.key];
};

function SortableRow({ row, columns }) {
  const rowId = getRowId(row);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#f0f7ff" : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? "sortable-drag" : ""}>
      <td className="drag-cell">
        <button
          type="button"
          className="drag-handle"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={18} color="#6b7280" />
        </button>
      </td>
      {columns.map((column) => (
        <td
          key={`${rowId}-${column.key}`}
          className={column.className}
        >
          {renderCellValue(row, column)}
        </td>
      ))}
    </tr>
  );
}

const SortableDatatable = ({
  data = [],
  columns = [],
  onReorder,
  className = "",
  title = null,
  disabled = false,
}) => {
  const tableData = Array.isArray(data) ? data : [];
  const itemIds = tableData.map(getRowId);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || disabled) return;

    const oldIndex = itemIds.indexOf(String(active.id));
    const newIndex = itemIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(tableData, oldIndex, newIndex).map(
      (item, index) => ({ ...item, order: index })
    );
    onReorder?.(reordered);
  };

  if (tableData.length === 0) {
    return (
      <div className={`table-container ${className}`}>
        {title && <h3 className="table-title">{title}</h3>}
        <p className="no-data">No data available</p>
      </div>
    );
  }

  return (
    <div className={`table-container ${className}`}>
      {title && <h3 className="table-title">{title}</h3>}
      <div className="table-wrapper">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="user-table">
            <thead>
              <tr>
                <th style={{ width: 48 }} aria-label="Reorder" />
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {tableData.map((row) => (
                  <SortableRow
                    key={getRowId(row)}
                    row={row}
                    columns={columns}
                  />
                ))}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </div>
    </div>
  );
};

export default SortableDatatable;
